import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPlus, 
  faSearch, 
  faUsers, 
  faUserPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faUserMinus,
  faTeamspeak,
  faBuilding,
  faUserFriends
} from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './TeamManagementPage.css';

const TeamManagementPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // State for active section
  const [activeSection, setActiveSection] = useState('teams'); // 'teams', 'create', 'members'

  // State for teams
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // State for create/edit team
  const [teamForm, setTeamForm] = useState({
    name: '',
    subdomain: '',
    smallImageLight: null,
    smallImageDark: null,
    largeImageLight: null,
    largeImageDark: null
  });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // State for team members
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // State for assign user
  const [assignUserEmail, setAssignUserEmail] = useState('');
  const [assigningUser, setAssigningUser] = useState(false);

  // Load teams when component mounts
  useEffect(() => {
    fetchTeams();
  }, []);

  // Load team members when a team is selected
  useEffect(() => {
    if (selectedTeam && activeSection === 'members') {
      fetchTeamMembers(selectedTeam.name);
    }
  }, [selectedTeam, activeSection]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch teams');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setTeams(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load teams');
      }

    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async (teamName) => {
    try {
      setLoadingMembers(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/v1/user/team/members?teamName=${encodeURIComponent(teamName)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch team members');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setTeamMembers(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load team members');
      }

    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(err.message);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    
    try {
      setCreatingTeam(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const formData = new FormData();
      formData.append('name', teamForm.name);
      formData.append('subdomain', teamForm.subdomain);
      formData.append('smallImageLight', teamForm.smallImageLight);
      formData.append('smallImageDark', teamForm.smallImageDark);
      formData.append('largeImageLight', teamForm.largeImageLight);
      formData.append('largeImageDark', teamForm.largeImageDark);

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create team');
      }

      if (data.status === 'success') {
        setSuccessMessage('Team created successfully!');
        resetForm();
        await fetchTeams();
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to create team');
      }

    } catch (err) {
      console.error('Error creating team:', err);
      setError(err.message);
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleUpdateTeam = async () => {
    if (!editingTeam) return;
    
    try {
      setCreatingTeam(true); // Reuse loading state
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const formData = new FormData();
      formData.append('name', teamForm.name);
      formData.append('subdomain', teamForm.subdomain);
      
      // Only append images if they are selected
      if (teamForm.smallImageLight) {
        formData.append('smallImageLight', teamForm.smallImageLight);
      }
      if (teamForm.smallImageDark) {
        formData.append('smallImageDark', teamForm.smallImageDark);
      }
      if (teamForm.largeImageLight) {
        formData.append('largeImageLight', teamForm.largeImageLight);
      }
      if (teamForm.largeImageDark) {
        formData.append('largeImageDark', teamForm.largeImageDark);
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams/${editingTeam.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update team');
      }

      if (data.status === 'success') {
        setSuccessMessage('Team updated successfully!');
        closeEditModal();
        await fetchTeams();
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to update team');
      }

    } catch (err) {
      console.error('Error updating team:', err);
      setError(err.message);
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams/${teamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete team');
      }

      if (data.status === 'success') {
        setSuccessMessage('Team deleted successfully!');
        await fetchTeams();
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to delete team');
      }

    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err.message);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    
    try {
      setAssigningUser(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/v1/user/team/assign?email=${encodeURIComponent(assignUserEmail)}&teamName=${encodeURIComponent(selectedTeam.name)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign user to team');
      }

      if (data.status === 'success') {
        setSuccessMessage(`User ${assignUserEmail} assigned to team successfully!`);
        setAssignUserEmail('');
        await fetchTeamMembers(selectedTeam.name);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to assign user to team');
      }

    } catch (err) {
      console.error('Error assigning user:', err);
      setError(err.message);
    } finally {
      setAssigningUser(false);
    }
  };

  const handleRemoveUser = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${userEmail} from the team?`)) {
      return;
    }

    try {
      setError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/v1/user/team/remove?email=${encodeURIComponent(userEmail)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to remove user from team');
      }

      if (data.status === 'success') {
        setSuccessMessage(`User ${userEmail} removed from team successfully!`);
        await fetchTeamMembers(selectedTeam.name);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to remove user from team');
      }

    } catch (err) {
      console.error('Error removing user:', err);
      setError(err.message);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const formatDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
    const [year, month, day, hour, minute, second] = dateArray;
    return new Date(year, month - 1, day, hour, minute, second).toLocaleString();
  };

  const filteredMembers = teamMembers.filter(member =>
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.iboName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.iboNumber.includes(searchTerm)
  );

  const renderTeamsList = () => (
    <div className="teams-list-section">
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faBuilding} />
          All Teams
        </h3>
        <button
          onClick={() => setActiveSection('create')}
          className="create-team-button"
        >
          <FontAwesomeIcon icon={faPlus} />
          Create Team
        </button>
      </div>

      {loading && <div className="loading-indicator">Loading teams...</div>}
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {!loading && teams.length === 0 && !error && (
        <div className="no-teams">
          <p>No teams found.</p>
          <p>Create your first team to get started.</p>
        </div>
      )}

      {!loading && teams.length > 0 && (
        <div className="teams-grid">
          {teams.map(team => (
            <div key={team.id} className="team-card">
              <div className="team-card-header">
                <div className="team-info">
                  <h4>{team.name}</h4>
                  {team.subdomain && <p className="team-subdomain">Subdomain: {team.subdomain}</p>}
                  <p className="team-date">Created: {formatDate(team.createdAt)}</p>
                </div>
                <div className="team-actions">
                  <button
                    onClick={() => handleEditTeam(team)}
                    className="edit-button"
                    title="Edit Team"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDeleteTeam(team.id)}
                    className="delete-button"
                    title="Delete Team"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              
              {/* Team Images */}
              {(team.smallImageLightUrl || team.smallImageDarkUrl || team.largeImageLightUrl || team.largeImageDarkUrl) && (
                <div className="team-images">
                  <h5>Team Images</h5>
                  <div className="image-grid">
                    {team.smallImageLightUrl && (
                      <div className="image-item">
                        <img src={team.smallImageLightUrl} alt="Small Light" className="team-image small" />
                        <span className="image-label">Small Light</span>
                      </div>
                    )}
                    {team.smallImageDarkUrl && (
                      <div className="image-item">
                        <img src={team.smallImageDarkUrl} alt="Small Dark" className="team-image small" />
                        <span className="image-label">Small Dark</span>
                      </div>
                    )}
                    {team.largeImageLightUrl && (
                      <div className="image-item">
                        <img src={team.largeImageLightUrl} alt="Large Light" className="team-image large" />
                        <span className="image-label">Large Light</span>
                      </div>
                    )}
                    {team.largeImageDarkUrl && (
                      <div className="image-item">
                        <img src={team.largeImageDarkUrl} alt="Large Dark" className="team-image large" />
                        <span className="image-label">Large Dark</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="team-card-footer">
                <button
                  onClick={() => {
                    setSelectedTeam(team);
                    setActiveSection('members');
                  }}
                  className="manage-members-button"
                >
                  <FontAwesomeIcon icon={faUserFriends} />
                  Manage Members
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCreateTeam = () => (
    <div className="create-team-section">
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faPlus} />
          Create New Team
        </h3>
        <button
          onClick={() => setActiveSection('teams')}
          className="back-to-teams-button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Teams
        </button>
      </div>

      <form onSubmit={handleCreateTeam} className="create-team-form">
        <div className="form-group">
          <label htmlFor="teamName">Team Name *</label>
          <input
            type="text"
            id="teamName"
            value={teamForm.name}
            onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
            required
            placeholder="Enter team name"
            className="team-name-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="teamSubdomain">Subdomain *</label>
          <input
            type="text"
            id="teamSubdomain"
            value={teamForm.subdomain}
            onChange={(e) => setTeamForm({ ...teamForm, subdomain: e.target.value })}
            required
            placeholder="Enter team subdomain (e.g., team-alpha)"
            className="team-name-input"
          />
          <p className="field-description">The subdomain will be used for team-specific URLs</p>
        </div>

        <div className="form-group">
          <label htmlFor="smallImageLight">Small Light Image (for light mode)</label>
          <p className="field-description">Upload a small logo/image for light theme (recommended: 100x100px)</p>
          <input
            type="file"
            id="smallImageLight"
            accept="image/*"
            onChange={(e) => handleFileSelect('smallImageLight', e.target.files[0])}
            className="file-input"
          />
          {teamForm.smallImageLight && (
            <img src={URL.createObjectURL(teamForm.smallImageLight)} alt="Preview" className="preview-image" />
          )}
        </div>

        <div className="form-group">
          <label htmlFor="smallImageDark">Small Dark Image (for dark mode)</label>
          <p className="field-description">Upload a small logo/image for dark theme (recommended: 100x100px)</p>
          <input
            type="file"
            id="smallImageDark"
            accept="image/*"
            onChange={(e) => handleFileSelect('smallImageDark', e.target.files[0])}
            className="file-input"
          />
          {teamForm.smallImageDark && (
            <img src={URL.createObjectURL(teamForm.smallImageDark)} alt="Preview" className="preview-image" />
          )}
        </div>

        <div className="form-group">
          <label htmlFor="largeImageLight">Large Light Image (for light mode)</label>
          <p className="field-description">Upload a large logo/image for light theme (recommended: 300x300px)</p>
          <input
            type="file"
            id="largeImageLight"
            accept="image/*"
            onChange={(e) => handleFileSelect('largeImageLight', e.target.files[0])}
            className="file-input"
          />
          {teamForm.largeImageLight && (
            <img src={URL.createObjectURL(teamForm.largeImageLight)} alt="Preview" className="preview-image" />
          )}
        </div>

        <div className="form-group">
          <label htmlFor="largeImageDark">Large Dark Image (for dark mode)</label>
          <p className="field-description">Upload a large logo/image for dark theme (recommended: 300x300px)</p>
          <input
            type="file"
            id="largeImageDark"
            accept="image/*"
            onChange={(e) => handleFileSelect('largeImageDark', e.target.files[0])}
            className="file-input"
          />
          {teamForm.largeImageDark && (
            <img src={URL.createObjectURL(teamForm.largeImageDark)} alt="Preview" className="preview-image" />
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={creatingTeam}
            className="submit-button"
          >
            {creatingTeam ? (
              <>Creating Team...</>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} />
                Create Team
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderTeamMembers = () => (
    <div className="team-members-section">
      <div className="section-header">
        <div className="header-left">
          <h3>
            <FontAwesomeIcon icon={faUserFriends} />
            {selectedTeam?.name} Members
          </h3>
          <button
            onClick={() => setActiveSection('teams')}
            className="back-to-teams-button"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Teams
          </button>
        </div>
        <div className="search-container">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Assign User Form */}
      <div className="assign-user-section">
        <h4>Assign User to Team</h4>
        <form onSubmit={handleAssignUser} className="assign-user-form">
          <div className="form-row">
            <input
              type="email"
              value={assignUserEmail}
              onChange={(e) => setAssignUserEmail(e.target.value)}
              placeholder="Enter user email"
              required
              className="email-input"
            />
            <button
              type="submit"
              disabled={assigningUser}
              className="assign-button"
            >
              {assigningUser ? (
                <>Assigning...</>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUserPlus} />
                  Assign User
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {loadingMembers && <div className="loading-indicator">Loading team members...</div>}

      {!loadingMembers && filteredMembers.length === 0 && (
        <div className="no-members">
          <p>No members found in this team.</p>
          {searchTerm && <p>Try adjusting your search criteria.</p>}
        </div>
      )}

      {!loadingMembers && filteredMembers.length > 0 && (
        <div className="members-table-container">
          <table className="members-table">
            <thead>
              <tr>
                <th>IBO Number</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map(member => (
                <tr key={member.id}>
                  <td className="ibo-number">{member.iboNumber}</td>
                  <td className="name">{member.iboName || 'N/A'}</td>
                  <td className="email">{member.email}</td>
                  <td className="phone">{member.phone || 'N/A'}</td>
                  <td className="status">
                    <div className="status-badges">
                      <span className={`status-badge ${member.emailVerified ? 'verified' : 'unverified'}`}>
                        Email {member.emailVerified ? 'Verified' : 'Unverified'}
                      </span>
                      <span className={`status-badge ${member.userVerified ? 'verified' : 'unverified'}`}>
                        {member.userVerified ? 'Active' : 'Inactive'}
                      </span>
                      {member.firstContractYear && (
                        <span className="status-badge first-year">First Year</span>
                      )}
                    </div>
                  </td>
                  <td className="actions">
                    <button
                      onClick={() => handleRemoveUser(member.email)}
                      className="remove-user-button"
                      title="Remove from team"
                    >
                      <FontAwesomeIcon icon={faUserMinus} />
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Helper function to handle file selection with preview
  const handleFileSelect = (fieldName, file) => {
    setTeamForm(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  // Reset form and previews
  const resetForm = () => {
    setTeamForm({
      name: '',
      subdomain: '',
      smallImageLight: null,
      smallImageDark: null,
      largeImageLight: null,
      largeImageDark: null
    });
  };

  // Handle team editing with images
  const handleEditTeam = (team) => {
    setEditingTeam({...team});
    setTeamForm({
      name: team.name,
      subdomain: team.subdomain || '',
      smallImageLight: null,
      smallImageDark: null,
      largeImageLight: null,
      largeImageDark: null
    });
    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingTeam(null);
    resetForm();
  };

  return (
    <div className="team-management-container">
      <div className="page-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Dashboard
        </button>
        <h2>Team Management</h2>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Section Navigation */}
      <div className="section-navigation">
        <button
          className={`section-button ${activeSection === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveSection('teams')}
        >
          <FontAwesomeIcon icon={faBuilding} />
          Teams
        </button>
        <button
          className={`section-button ${activeSection === 'create' ? 'active' : ''}`}
          onClick={() => setActiveSection('create')}
        >
          <FontAwesomeIcon icon={faPlus} />
          Create Team
        </button>
        {selectedTeam && (
          <button
            className={`section-button ${activeSection === 'members' ? 'active' : ''}`}
            onClick={() => setActiveSection('members')}
          >
            <FontAwesomeIcon icon={faUserFriends} />
            {selectedTeam.name} Members
          </button>
        )}
      </div>

      {/* Section Content */}
      <div className="section-content">
        {activeSection === 'teams' && renderTeamsList()}
        {activeSection === 'create' && renderCreateTeam()}
        {activeSection === 'members' && renderTeamMembers()}
      </div>

      {/* Edit Team Modal */}
      {showEditModal && editingTeam && (
        <div className="modal-overlay">
          <div className="modal-content edit-team-modal">
            <div className="modal-header">
              <h3>Edit Team: {editingTeam.name}</h3>
              <button 
                onClick={closeEditModal}
                className="close-modal-btn"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateTeam(); }} className="edit-team-form-modal">
                <div className="form-group">
                  <label htmlFor="editTeamName">Team Name *</label>
                  <input
                    type="text"
                    id="editTeamName"
                    value={teamForm.name}
                    onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                    required
                    placeholder="Enter team name"
                    className="team-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editTeamSubdomain">Subdomain *</label>
                  <input
                    type="text"
                    id="editTeamSubdomain"
                    value={teamForm.subdomain}
                    onChange={(e) => setTeamForm({ ...teamForm, subdomain: e.target.value })}
                    required
                    placeholder="Enter team subdomain (e.g., team-alpha)"
                    className="team-name-input"
                  />
                  <p className="field-description">The subdomain will be used for team-specific URLs</p>
                </div>

                <div className="form-group">
                  <label htmlFor="editSmallImageLight">Small Light Image (for light mode)</label>
                  <p className="field-description">Upload a new small logo/image for light theme (recommended: 100x100px)</p>
                  <input
                    type="file"
                    id="editSmallImageLight"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('smallImageLight', e.target.files[0])}
                    className="file-input"
                  />
                  {teamForm.smallImageLight && (
                    <img src={URL.createObjectURL(teamForm.smallImageLight)} alt="Preview" className="preview-image" />
                  )}
                  {editingTeam.smallImageLightUrl && !teamForm.smallImageLight && (
                    <div className="current-image">
                      <p className="current-image-label">Current image:</p>
                      <img src={editingTeam.smallImageLightUrl} alt="Current Small Light" className="current-team-image" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="editSmallImageDark">Small Dark Image (for dark mode)</label>
                  <p className="field-description">Upload a new small logo/image for dark theme (recommended: 100x100px)</p>
                  <input
                    type="file"
                    id="editSmallImageDark"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('smallImageDark', e.target.files[0])}
                    className="file-input"
                  />
                  {teamForm.smallImageDark && (
                    <img src={URL.createObjectURL(teamForm.smallImageDark)} alt="Preview" className="preview-image" />
                  )}
                  {editingTeam.smallImageDarkUrl && !teamForm.smallImageDark && (
                    <div className="current-image">
                      <p className="current-image-label">Current image:</p>
                      <img src={editingTeam.smallImageDarkUrl} alt="Current Small Dark" className="current-team-image" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="editLargeImageLight">Large Light Image (for light mode)</label>
                  <p className="field-description">Upload a new large logo/image for light theme (recommended: 300x300px)</p>
                  <input
                    type="file"
                    id="editLargeImageLight"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('largeImageLight', e.target.files[0])}
                    className="file-input"
                  />
                  {teamForm.largeImageLight && (
                    <img src={URL.createObjectURL(teamForm.largeImageLight)} alt="Preview" className="preview-image" />
                  )}
                  {editingTeam.largeImageLightUrl && !teamForm.largeImageLight && (
                    <div className="current-image">
                      <p className="current-image-label">Current image:</p>
                      <img src={editingTeam.largeImageLightUrl} alt="Current Large Light" className="current-team-image" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="editLargeImageDark">Large Dark Image (for dark mode)</label>
                  <p className="field-description">Upload a new large logo/image for dark theme (recommended: 300x300px)</p>
                  <input
                    type="file"
                    id="editLargeImageDark"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('largeImageDark', e.target.files[0])}
                    className="file-input"
                  />
                  {teamForm.largeImageDark && (
                    <img src={URL.createObjectURL(teamForm.largeImageDark)} alt="Preview" className="preview-image" />
                  )}
                  {editingTeam.largeImageDarkUrl && !teamForm.largeImageDark && (
                    <div className="current-image">
                      <p className="current-image-label">Current image:</p>
                      <img src={editingTeam.largeImageDarkUrl} alt="Current Large Dark" className="current-team-image" />
                    </div>
                  )}
                </div>
              </form>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={handleUpdateTeam}
                className="update-team-btn"
                disabled={creatingTeam}
              >
                {creatingTeam ? (
                  <>Updating Team...</>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEdit} />
                    Update Team
                  </>
                )}
              </button>
              <button 
                onClick={closeEditModal}
                className="cancel-btn"
                disabled={creatingTeam}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage; 